/**
 * 사용자 가이드 (?page=guide&doc=<slug>) — 문서 레지스트리(guideDocs.jsx) 기반
 * 다중 페이지(1기능=1문서, Claude Docs 구조). 로그인 무관 공개.
 * 구 단일 페이지 앵커(#sec-N) 딥링크·가이드 QA 근거 칩은 ANCHOR_TO_DOC 으로 하위호환.
 */
import React, { useState, useEffect } from "react";
import ApiPortalLayout, { SideTocItem } from "./ApiPortalLayout";
import { GUIDE_DOCS, S } from "./guideDocs";

/** 가이드에게 질문하기 — NotebookLM 컨셉 자체 구현.
 *  POST /api/guide/qa → {answer, sections} — 근거 섹션 칩(클릭=해당 문서로 이동). */
function GuideQaBox({ onNavigate }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const ask = async () => {
    const question = q.trim();
    if (question.length < 2 || loading) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      const r = await fetch("/api/guide/qa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      const d = await r.json();
      if (!r.ok) {
        setErr(
          r.status === 429
            ? `질문이 잦아 잠시 쉬어갑니다 — ${d.retryAfterSeconds || 60}초 후 다시 시도해 주세요.`
            : d.message || "답변 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        );
      } else {
        setResult(d);
      }
    } catch (e) {
      setErr("네트워크 오류 — 잠시 후 다시 시도해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  const goSection = (s) => {
    if (s.page === "guide") {
      const el = document.querySelector(s.anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = `/?page=${s.page}${s.anchor}`;
    }
  };

  return (
    <div
      style={{
        border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 18px",
        margin: "18px 0 6px", background: "#f8fafc",
      }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 8 }}>
        가이드에게 질문하기
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
          maxLength={300}
          placeholder="예: API 키를 잃어버리면 어떻게 하나요? / 보류는 무슨 뜻인가요?"
          style={{
            flex: 1, padding: "10px 12px", fontSize: 14, borderRadius: 8,
            border: "1px solid #d1d5db", outline: "none", background: "#fff",
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={ask}
          disabled={loading || q.trim().length < 2}
          style={{
            background: loading ? "#9ca3af" : "#111827", color: "#fff", border: "none",
            borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600,
            cursor: loading ? "default" : "pointer", whiteSpace: "nowrap",
          }}
        >
          {loading ? "답변 중…" : "질문"}
        </button>
      </div>
      {err && (
        <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>{err}</div>
      )}
      {result && (
        <div
          style={{
            marginTop: 12, background: "#fff", border: "1px solid #e5e7eb",
            borderRadius: 8, padding: "12px 14px",
          }}
        >
          <div style={{ fontSize: 14, color: "#1f2937", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {result.answer}
          </div>
          {result.sections?.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>근거:</span>
              {result.sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => goSection(s)}
                  style={{
                    fontSize: 12.5, padding: "3px 10px", borderRadius: 12,
                    border: "1px solid #d1d5db", background: "#f9fafb",
                    color: "#374151", cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  {s.page === "api-docs" ? "API 문서 · " : ""}{s.title} ↗
                </button>
              ))}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11.5, color: "#9ca3af" }}>
            AI 답변은 이 가이드·API 문서 내용만 근거로 하며 부정확할 수 있습니다 — 확실한 안내가
            필요하면{" "}
            <a href="/?page=contact" onClick={(e) => { e.preventDefault(); onNavigate("contact"); }}>
              문의하기
            </a>
            를 이용해 주세요.
          </div>
        </div>
      )}
    </div>
  );
}

export default function UserGuidePage({ onNavigate }) {
  // 단일 스크롤 페이지(사용자 결정) — 우측 목록 클릭=해당 섹션 앵커 이동.
  // ?doc=<slug> 딥링크·#sec-N 구 앵커는 mount 시 해당 섹션으로 스크롤.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("doc");
    const target = q ? `#doc-${q}` : window.location.hash;
    if (target) {
      setTimeout(() => {
        const el = document.querySelector(target);
        if (el) el.scrollIntoView({ block: "start" });
      }, 60);
    }
  }, []);

  return (
    <ApiPortalLayout
      active="guide"
      onNavigate={onNavigate}
      toc={GUIDE_DOCS.map((g) => (
        <SideTocItem key={g.slug} href={`#doc-${g.slug}`}>{g.title}</SideTocItem>
      ))}
    >
      <div style={S.page}>
        {GUIDE_DOCS.map((g, i) => (
          <section
            key={g.slug}
            id={`doc-${g.slug}`}
            style={{ scrollMarginTop: 72, marginBottom: 48,
              borderTop: i > 0 ? "1px solid #e5e7eb" : "none",
              paddingTop: i > 0 ? 32 : 0 }}
          >
            {g.body({ onNavigate })}
            {g.slug === "intro" && <GuideQaBox onNavigate={onNavigate} />}
          </section>
        ))}
      </div>
    </ApiPortalLayout>
  );
}
